<template>
  <div class="actor-changes fixed-heading" :class="{ 'change-single': actorId }">
    <div class="heading">
      <router-link v-if="actorId" :to="'/actors/' + actorId" class="user-change-back">
        <icon name="arrow-left" size="14"/>
        <span>Back to profile</span>
      </router-link>
      <h1>Changes by users</h1>
      <p class="guide_text">
        Every change a user made to a profile, who made it and from which ecosystem. Changes
        made by enrichers and automatic processing are not part of this overview.
      </p>
    </div>

    <div class="scrollable has-padding">
      <div class="w-full space-y-4">

        <!-- ── Filter bar ── -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 tw-flex flex-wrap items-center gap-3">
          <button
            class="user-change-filter"
            :class="filters.unexpected ? 'user-change-filter--active' : ''"
            @click="toggleUnexpectedFilter"
          >
            <icon name="warning" size="14"/>
            <span>Only unexpected changes</span>
            <span v-if="unexpectedTotal" class="user-change-filter__count">{{ unexpectedTotal }}</span>
          </button>

          <span class="text-sm text-gray-500 ml-auto" v-if="total !== null">
            {{ total }} {{ total === 1 ? 'change' : 'changes' }}
          </span>
        </div>

        <!-- ── Skeleton ── -->
        <template v-if="loading">
          <div
            v-for="i in 3" :key="'skeleton-' + i"
            class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse"
          >
            <div class="tw-flex items-center justify-between mb-4">
              <div class="h-4 w-48 bg-gray-200 rounded"/>
              <div class="h-3 w-20 bg-gray-200 rounded"/>
            </div>
            <div class="space-y-3">
              <div v-for="j in 2" :key="j" class="h-3 w-3/4 bg-gray-200 rounded"/>
            </div>
          </div>
        </template>

        <!-- ── Empty state ── -->
        <div
          v-else-if="!entries.length"
          class="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center"
        >
          <p class="text-sm text-gray-500">
            {{ filters.unexpected ? 'No unexpected changes were found.' : 'No changes by users yet.' }}
          </p>
        </div>

        <!-- ── Entries ── -->
        <template v-else>
        <div
          v-for="entry in highlightedEntries" :key="entry.id"
          class="bg-white rounded-xl border shadow-sm overflow-hidden mb-4"
          :class="entry.unexpected ? 'border-red-200' : 'border-gray-100'"
        >
          <!-- Entry header -->
          <div class="p-5 pb-4">
            <div class="tw-flex flex-wrap items-start justify-between gap-3">
              <div>
                <div class="tw-flex flex-wrap items-center gap-2">
                  <span class="text-sm font-semibold text-gray-900">{{ entry.user_name || 'Unknown user' }}</span>
                  <span class="user-change-badge" v-if="entry.user_role">{{ entry.user_role }}</span>
                  <span class="user-change-badge user-change-badge--muted" v-else>no role</span>
                  <span class="user-change-badge user-change-badge--info" v-if="entry.user_has_claimed_actor">claimed</span>
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  <span v-if="entry.user_email">{{ entry.user_email }}</span>
                  <span v-if="!actorId && entry.actor_name">
                    &middot; changed
                    <router-link
                      v-if="entry.actor_elastic_id"
                      :to="'/actors/' + entry.actor_elastic_id"
                      class="text-gray-700 underline"
                    >{{ entry.actor_name }}</router-link>
                    <span v-else>{{ entry.actor_name }} (removed)</span>
                  </span>
                </div>
              </div>

              <div class="text-right text-xs text-gray-500 shrink-0">
                <div :data-tooltip="entry.created_at" data-position="top center">{{ timeAgo(entry.created_at) }}</div>
                <div class="mt-1" v-if="entry.agent">via {{ fromAgent(entry.agent) }}</div>
              </div>
            </div>

            <!-- Why this entry is suspicious -->
            <div v-if="entry.unexpected" class="user-change-alert">
              <icon name="warning" size="14"/>
              <span>
                <template v-if="entry.cross_ecosystem">
                  This change was made from the {{ entry.ecosystem_name }} ecosystem, which is not the
                  ecosystem of the profile.
                </template>
                <template v-else>
                  This user has no verified membership of the ecosystem of this profile.
                </template>
              </span>
            </div>
          </div>

          <!-- Changed properties -->
          <div class="border-t border-gray-100 divide-y divide-gray-100">
            <div
              v-for="change in entry.changes" :key="entry.id + '-' + change.property"
              class="px-5 py-3 tw-flex flex-wrap items-start gap-3"
            >
              <div class="w-40 shrink-0">
                <span
                  class="text-xs font-semibold text-gray-700 break-words"
                  :data-tooltip="change.property" data-position="top center"
                >{{ humanizeProperty(change.property) }}</span>
              </div>

              <div class="flex-1 min-w-0 text-sm text-gray-700 break-words">
                <div class="user-change-value">
                  <span class="user-change-value__label">was</span>
                  <span
                    class="user-change-delta"
                    :class="{ 'user-change-delta--empty': change.highlight.isEmptyBefore }"
                    v-html="change.highlight.before"
                  />
                </div>
                <div class="user-change-value">
                  <span class="user-change-value__label user-change-value__label--now">now</span>
                  <span
                    class="user-change-delta"
                    :class="{ 'user-change-delta--empty': change.highlight.isEmptyAfter }"
                    v-html="change.highlight.after"
                  />
                </div>
              </div>

              <div class="shrink-0">
                <button
                  v-if="change.revertable && canRevert(entry)"
                  class="user-change-revert"
                  :class="isConfirming(entry, change.property) ? 'user-change-revert--confirm' : ''"
                  :disabled="reverting === entry.id"
                  @click="revert(entry, change.property)"
                >
                  <icon name="undo" size="14"/>
                  <span>{{ isConfirming(entry, change.property) ? 'Confirm revert' : 'Revert' }}</span>
                </button>
                <span
                  v-else-if="!change.revertable"
                  class="text-xs text-gray-400"
                  data-tooltip="The original value of this change was not stored in full"
                  data-position="top center"
                >not revertable</span>
              </div>
            </div>
          </div>

          <!-- Entry footer -->
          <div class="px-5 py-3 bg-gray-50 tw-flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span v-if="entry.method && entry.route">{{ entry.method }} /{{ entry.route }}</span>
            <span v-if="entry.ip_address">&middot; {{ entry.ip_address }}</span>

            <button
              v-if="canRevert(entry) && revertableProperties(entry).length > 1"
              class="user-change-revert ml-auto"
              :class="isConfirming(entry, ALL_PROPERTIES) ? 'user-change-revert--confirm' : ''"
              :disabled="reverting === entry.id"
              @click="revert(entry, ALL_PROPERTIES)"
            >
              <icon name="undo" size="14"/>
              <span>{{ isConfirming(entry, ALL_PROPERTIES) ? 'Confirm revert of all' : 'Revert all' }}</span>
            </button>
          </div>

          <!-- Result of a revert -->
          <div v-if="feedback[entry.id]" class="px-5 py-3 text-xs" :class="feedback[entry.id].error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'">
            {{ feedback[entry.id].message }}
          </div>
        </div>
        </template>
      </div>
    </div>

    <pagination :paging="paging" @change="setOffset"/>
  </div>
</template>

<script lang="ts">
import Pagination from '../Pagination/SimplePagination.vue'

import { fetchUserChanges, revertUserChange } from '../../api/heartbeat.js'
import { highlightChange, humanizeProperty } from '../../util/changeValues.js'
import { fromAgent } from '../../util/helpers.js'
import { timeAgo } from '../../util/date'
import { defineComponent } from 'vue'

// Sentinel used to revert every property of an entry at once
const ALL_PROPERTIES = '*'

export default defineComponent({
  components: {
    Pagination
  },
  props: ['actor-id'],
  data () {
    return {
      ALL_PROPERTIES,
      entries: [] as any[],
      paging: null as any,
      total: null as number | null,
      unexpectedTotal: 0,
      loading: true,
      // The entry that is currently being reverted, disables its buttons
      reverting: null as number | null,
      // A revert is only performed after a second click on the same button
      confirming: null as string | null,
      feedback: {} as Record<string, { message: string, error: boolean }>,
      filters: {
        actorId: this.actorId || null,
        unexpected: false,
        limit: 25,
        offset: 0
      }
    }
  },
  computed: {
    // The diff of every change is built once here instead of in the template,
    // so it is not recalculated on every render
    highlightedEntries () {
      return this.entries.map(entry => ({
        ...entry,
        changes: (entry.changes || []).map(change => ({
          ...change,
          highlight: highlightChange(change.from, change.to)
        }))
      }))
    }
  },
  methods: {
    fromAgent,
    timeAgo,
    fetch () {
      this.loading = true

      fetchUserChanges(this.filters)
        .then(({ data, paging }) => {
          this.entries = data.entries || []
          this.total = data.total
          this.unexpectedTotal = data.unexpected_total || 0
          this.paging = paging
          this.loading = false
        })
        .catch(() => {
          this.entries = []
          this.loading = false
        })
    },
    setOffset (offset) {
      this.filters.offset = offset
      this.fetch()
    },
    toggleUnexpectedFilter () {
      this.filters.unexpected = !this.filters.unexpected
      this.filters.offset = 0
      this.fetch()
    },
    // Only the ecosystem the profile belongs to can revert its changes, a change that was
    // made from another ecosystem can be reverted here, one made on another ecosystem's
    // profile cannot
    canRevert (entry) {
      return entry.actor_in_ecosystem && this.revertableProperties(entry).length > 0
    },
    revertableProperties (entry) {
      return (entry.changes || []).filter(change => change.revertable)
    },
    isConfirming (entry, property) {
      return this.confirming === entry.id + '-' + property
    },
    revert (entry, property) {
      // First click asks for confirmation, second click performs the revert
      if (!this.isConfirming(entry, property)) {
        this.confirming = entry.id + '-' + property
        return
      }

      this.confirming = null
      this.reverting = entry.id

      const properties = property === ALL_PROPERTIES
        ? this.revertableProperties(entry).map(change => change.property)
        : [property]

      revertUserChange(entry.id, properties)
        .then(response => {
          this.reverting = null
          this.setFeedback(entry, 'Reverted ' + (response.reverted || []).join(', ') + '.', false)
          this.fetch()
        })
        .catch(error => {
          this.reverting = null
          this.setFeedback(entry, (error && error.message) || 'The change could not be reverted.', true)
        })
    },
    setFeedback (entry, message, error) {
      this.feedback = Object.assign({}, this.feedback, { [entry.id]: { message, error } })
    },
    humanizeProperty
  },
  watch: {
    actorId () {
      this.filters.actorId = this.actorId || null
      this.filters.offset = 0
      this.fetch()
    }
  },
  mounted () {
    this.fetch()
  }
})
</script>

<style lang="scss" scoped>
.user-change-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;

  &:hover {
    color: #374151;
  }
}

.user-change-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  &--active {
    border-color: #fca5a5;
    background: #fef2f2;
    color: #b91c1c;
  }

  &__count {
    padding: 1px 6px;
    border-radius: 9999px;
    background: #b91c1c;
    color: #fff;
    font-size: 11px;
  }
}

.user-change-badge {
  padding: 2px 8px;
  border-radius: 9999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;

  &--muted {
    background: #fef2f2;
    color: #b91c1c;
    text-transform: none;
  }

  &--info {
    background: #eff6ff;
    color: #1d4ed8;
    text-transform: none;
  }
}

.user-change-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}

.user-change-revert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  &:disabled {
    opacity: 0.5;
  }

  &--confirm {
    border-color: #fca5a5;
    background: #fef2f2;
    color: #b91c1c;
  }
}

.user-change-value {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 2px 0;

  &__label {
    flex: 0 0 34px;
    padding-top: 1px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9ca3af;

    &--now {
      color: #15803d;
    }
  }
}

.user-change-delta {
  flex: 1 1 auto;
  min-width: 0;
  overflow: auto;
  max-height: 220px;
  // Keep the spaces and line breaks of the original value
  white-space: pre-wrap;
  word-break: break-word;
  word-wrap: break-word;

  &--empty {
    color: #9ca3af;
    font-style: italic;
  }

  // The removed and added parts, the padding keeps them from sticking
  // against the text around them
  :deep(del) {
    padding: 1px 3px;
    border-radius: 3px;
    margin: 0 1px;
    background: #fee2e2;
    color: #b91c1c;
    text-decoration: line-through;
  }

  :deep(ins) {
    padding: 1px 3px;
    border-radius: 3px;
    margin: 0 1px;
    background: #dcfce7;
    color: #15803d;
    text-decoration: none;
  }
}
</style>
